/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface LandingZoneOutOfServiceReasonProps {
  id: string;
  name: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class LandingZoneOutOfServiceReasonRecord extends Record<LandingZoneOutOfServiceReasonProps>(
  {
    id: null as any,
    name: null as any,
    dateLastUpdated: null as any,
    dateCreated: null as any,
    dateDeleted: null as any,
    lastUpdatedById: null as any,
  },
) {
  public static fromJS(json: {
    id: string;
    name: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): LandingZoneOutOfServiceReasonRecord {
    if (json == null) {
      throw new Error('LandingZoneOutOfServiceReasonRecord.fromJS: parameter cannot be null');
    }
    return new LandingZoneOutOfServiceReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'LandingZoneOutOfServiceReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'LandingZoneOutOfServiceReasonJson.name'),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [LandingZoneOutOfServiceReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('LandingZoneOutOfServiceReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new LandingZoneOutOfServiceReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'LandingZoneOutOfServiceReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'LandingZoneOutOfServiceReasonJson.name'),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: LandingZoneOutOfServiceReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('LandingZoneOutOfServiceReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class LandingZoneOutOfServiceReasonViewModelBase
  implements LandingZoneOutOfServiceReasonProps
{
  protected readonly _props: LandingZoneOutOfServiceReasonProps;
  private _json?: LandingZoneOutOfServiceReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: LandingZoneOutOfServiceReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllLandingZoneOutOfServiceReasonEntities = (state: AppState) =>
  state.entityStores.landingZoneOutOfServiceReason.entities;

export const selectLandingZoneOutOfServiceReasonById = (id: string) => (state: AppState) =>
  state.entityStores.landingZoneOutOfServiceReason.entities.get(id);

export const selectLandingZoneOutOfServiceReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.landingZoneOutOfServiceReason.meta.get(id);

export const selectLandingZoneOutOfServiceReasonAndMetadataById = (id: string) =>
  createSelector(
    selectLandingZoneOutOfServiceReasonById(id),
    selectLandingZoneOutOfServiceReasonMetadataById(id),
    (landingZoneOutOfServiceReason, meta) => ({ landingZoneOutOfServiceReason, meta }),
  );

export function selectLandingZoneOutOfServiceReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllLandingZoneOutOfServiceReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, LandingZoneOutOfServiceReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectLandingZoneOutOfServiceReasonEntitiesByName(name: string) {
  return createSelector(selectAllLandingZoneOutOfServiceReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}
//#endregion Selectors
